import type { ZodType } from "zod";
import {
  AcceptInviteResponseSchema,
  AdminUsersResponseSchema,
  AssetDetailResponseSchema,
  AssetListResponseSchema,
  AssetVersionsResponseSchema,
  CreateInviteResponseSchema,
  CreateProjectResponseSchema,
  DaemonConfigResponseSchema,
  DaemonDisksResponseSchema,
  DaemonMachinesResponseSchema,
  FoldersResponseSchema,
  InboxRequestCreatedSchema,
  InboxStatusSchema,
  AddPoolCredentialResponseSchema,
  ListInvitesResponseSchema,
  ListUserCredentialsResponseSchema,
  ProvisionScalewayResponseSchema,
  MeResponseSchema,
  MyCredentialsResponseSchema,
  OnboardResponseSchema,
  ProjectDetailResponseSchema,
  ProjectListResponseSchema,
  SetUserCredentialResponseSchema,
  type AcceptInviteResponse,
  type AssetDetailResponse,
  type AssetListQuery,
  type AssetListResponse,
  type AssetVersionsResponse,
  type CreateInviteResponse,
  type CreateProjectResponse,
  type DaemonConfigResponse,
  type DaemonDisksResponse,
  type DaemonMachinesResponse,
  type FoldersResponse,
  type InboxRequestCreated,
  type InboxStatus,
  type AddPoolCredentialResponse,
  type ProvisionScalewayResponse,
  type ListInvitesResponse,
  type ListUserCredentialsResponse,
  type MeResponse,
  type MyCredentialsResponse,
  type OnboardResponse,
  type ProjectDetailResponse,
  type ProjectListResponse,
  type SetUserCredentialResponse,
  type UserInviteRole,
} from "./types.js";

// 1.D.4 Web API-client. Cookie-pohjainen auth (same-origin via Vite proxy
// devissä, same-domain tuotannossa). Zod-validointi joka GET:ssa.

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

export class NotAuthenticatedError extends ApiError {
  constructor() {
    super("not_authenticated", 401, "not_authenticated");
    this.name = "NotAuthenticatedError";
  }
}

export type ApiClientOptions = {
  /** Pohja jolle path liitetaan. Default = window.location.origin. */
  baseUrl?: string;
  /** Korvaa fetch testeissa. Default = global fetch. */
  fetch?: typeof fetch;
};

type ApiCallInit = Omit<RequestInit, "body" | "method"> & {
  query?: Record<string, string | number | undefined>;
};

export class ApiClient {
  private readonly baseUrl: string;
  private readonly fetchFn: typeof fetch;
  // UM.6.B: getter joka palauttaa scope-headerin arvon kullakin pyynnolla.
  // ViewScopeProvider asettaa tämän, oletuksena "self".
  private viewScopeProvider: () => string = () => "self";

  constructor(opts: ApiClientOptions = {}) {
    this.baseUrl = (
      opts.baseUrl ?? (typeof window !== "undefined" ? window.location.origin : "")
    ).replace(/\/+$/, "");
    this.fetchFn = opts.fetch ?? globalThis.fetch.bind(globalThis);
  }

  /** UM.6.B: rekisteröi scope-getter. Kutsutaan ViewScopeProvider:sta. */
  setViewScopeProvider(provider: () => string): void {
    this.viewScopeProvider = provider;
  }

  private viewScopeHeaders(): Record<string, string> {
    const scope = this.viewScopeProvider();
    return scope === "self" ? {} : { "X-Kratti-View-Scope": scope };
  }

  /** Kayttajan tiedot ja jasenyys-listat. Vaatii sisaankirjautumisen. */
  getMe(): Promise<MeResponse> {
    return this.get("/me", MeResponseSchema);
  }

  /** E1.3.c: käyttäjän oma pilvitunnus-status (secret EI mukana). */
  getMyCredentials(): Promise<MyCredentialsResponse> {
    return this.get("/me/credentials", MyCredentialsResponseSchema);
  }

  /** Asset-lista (palauttaa ACL-rajatun listan, manager nakee kaikki). */
  listAssets(query?: AssetListQuery): Promise<AssetListResponse> {
    const q: Record<string, string | number | undefined> = query
      ? {
          projectId: query.projectId,
          tier: query.tier,
          metaStatus: query.metaStatus,
          tags: query.tags,
          cursor: query.cursor,
          limit: query.limit,
          // 2.E.2 faceted-filterit + post-3.B path-prefix
          q: query.q,
          ratingMin: query.ratingMin,
          ratingMax: query.ratingMax,
          flag: query.flag,
          colorLabel: query.colorLabel,
          capturedFrom: query.capturedFrom,
          capturedTo: query.capturedTo,
          path: query.path,
        }
      : {};
    return this.get("/assets", AssetListResponseSchema, { query: q });
  }

  /** Yhden assetin detail-tiedot (metadata, tagit, versiot, sijainnit). */
  getAsset(id: string): Promise<AssetDetailResponse> {
    return this.get(`/assets/${encodeURIComponent(id)}`, AssetDetailResponseSchema);
  }

  /** Kansio-view-mode: alikansiot + tiedostot suoraan annetussa polussa.
   *  Hyväksyy samat faceted-filterit kuin listAssets. Filterit rajaavat
   *  sekä alikansioiden assetCount:ia että suoria tiedostoja. */
  listFolders(query?: AssetListQuery & { path?: string }): Promise<FoldersResponse> {
    const q: Record<string, string | number | undefined> = query
      ? {
          path: query.path,
          projectId: query.projectId,
          tier: query.tier,
          metaStatus: query.metaStatus,
          tags: query.tags,
          q: query.q,
          ratingMin: query.ratingMin,
          ratingMax: query.ratingMax,
          flag: query.flag,
          colorLabel: query.colorLabel,
          capturedFrom: query.capturedFrom,
          capturedTo: query.capturedTo,
        }
      : {};
    return this.get("/folders", FoldersResponseSchema, { query: q });
  }

  /** 3.B.1.5: snapshot-versio-historia + isIdentical-merkit. */
  getAssetVersions(id: string): Promise<AssetVersionsResponse> {
    return this.get(
      `/assets/${encodeURIComponent(id)}/versions`,
      AssetVersionsResponseSchema
    );
  }

  /** Soft-delete: deleted_at = NOW(). Asset poistuu Selaa-naekymasta. */
  async deleteAsset(id: string): Promise<void> {
    const url = `${this.baseUrl}/assets/${encodeURIComponent(id)}`;
    const res = await this.fetchFn(url, { method: "DELETE", credentials: "include" });
    if (res.status === 401) throw new NotAuthenticatedError();
    if (!res.ok) {
      const body = await safeJson(res);
      const code = typeof body?.error === "string" ? body.error : undefined;
      throw new ApiError(
        `api_error: ${res.status}${code ? ` (${code})` : ""}`,
        res.status,
        code
      );
    }
  }

  /**
   * Bulk-actions monille asseteille: delete, set-rating, set-color-label,
   * set-flag, move-to-project. Per-asset ACL → palauttaa { succeeded, failed }
   * (ei rollback).
   */
  async bulkAssets(input: {
    ids: string[];
    action:
      | { type: "delete" }
      | { type: "set-rating"; rating: number }
      | { type: "set-color-label"; colorLabel: "none" | "red" | "yellow" | "green" | "blue" | "purple" }
      | { type: "set-flag"; flag: "unflagged" | "pick" | "reject" }
      | { type: "move-to-project"; projectId: string | null };
  }): Promise<{ succeeded: string[]; failed: { id: string; reason: string }[] }> {
    const url = `${this.baseUrl}/assets/bulk`;
    const res = await this.fetchFn(url, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify(input),
    });
    if (res.status === 401) throw new NotAuthenticatedError();
    if (!res.ok) {
      const body = await safeJson(res);
      const code = typeof body?.error === "string" ? body.error : undefined;
      throw new ApiError(
        `api_error: ${res.status}${code ? ` (${code})` : ""}`,
        res.status,
        code
      );
    }
    return (await res.json()) as {
      succeeded: string[];
      failed: { id: string; reason: string }[];
    };
  }

  /** Projektit jotka kayttaja nakee (ownerina + member, manager kaikki). */
  listProjects(): Promise<ProjectListResponse> {
    return this.get("/projects", ProjectListResponseSchema);
  }

  /** Projektin detail-tiedot (members + access-taso). */
  getProject(id: string): Promise<ProjectDetailResponse> {
    return this.get(
      `/projects/${encodeURIComponent(id)}`,
      ProjectDetailResponseSchema
    );
  }

  /** Luo uusi projekti — nykyinen kayttaja owner. */
  createProject(name: string): Promise<CreateProjectResponse> {
    return this.post("/projects", { name }, CreateProjectResponseSchema);
  }

  /** 3.C.1 — forkkaa olemassa olevasta projektista uusi (samalla nimellä TAI annetulla).
   *  Uusi projekti perii vain rakenteen (ei assetteja), ownerId = pyytäjä,
   *  forkedFromProjectId viittaa lähteeseen. ACL: vaatii owner-tason
   *  source-projektiin (manager bypass:lla aina ok). */
  forkProject(sourceId: string, name: string): Promise<CreateProjectResponse> {
    return this.post(
      `/projects/${encodeURIComponent(sourceId)}/fork`,
      { name },
      CreateProjectResponseSchema
    );
  }

  /** 2.F.1 — arkistoi projekti (lifecycleState=archived). */
  async archiveProject(id: string): Promise<{ alreadyArchived?: boolean }> {
    const url = `${this.baseUrl}/projects/${encodeURIComponent(id)}/archive`;
    const res = await this.fetchFn(url, { method: "POST", credentials: "include" });
    if (res.status === 401) throw new NotAuthenticatedError();
    if (!res.ok) {
      const body = await safeJson(res);
      const code = typeof body?.error === "string" ? body.error : undefined;
      throw new ApiError(`api_error: ${res.status}`, res.status, code);
    }
    return (await res.json()) as { alreadyArchived?: boolean };
  }

  /** 2.F.3 — generoi Kopia-export-komennot. */
  async getExportInstructions(
    id: string,
    destPath?: string
  ): Promise<{ projectId: string; projectName: string; destPath: string; instructions: string }> {
    const url = `${this.baseUrl}/projects/${encodeURIComponent(id)}/export-instructions`;
    const body = destPath ? { destPath } : {};
    const res = await this.fetchFn(url, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    if (res.status === 401) throw new NotAuthenticatedError();
    if (!res.ok) {
      const errBody = await safeJson(res);
      const code = typeof errBody?.error === "string" ? errBody.error : undefined;
      throw new ApiError(`api_error: ${res.status}`, res.status, code);
    }
    return (await res.json()) as {
      projectId: string;
      projectName: string;
      destPath: string;
      instructions: string;
    };
  }

  /**
   * Onboardaa kirjautuneen kayttajan: jos jarjestelmassa ei ole viela
   * managereita, kayttaja saa rooliksi managerin (201). Jos kayttaja
   * itse on jo manager, palautetaan 200. Jos joku muu on jo manager,
   * heitetaan ApiError(409, "already_onboarded").
   */
  postOnboard(): Promise<OnboardResponse> {
    return this.post("/auth/onboard", undefined, OnboardResponseSchema);
  }

  // ─── 4.B.3.UM: invite-pohjainen kayttajahallinta ──────────────────────

  createInvite(email: string, role: UserInviteRole): Promise<CreateInviteResponse> {
    return this.post("/admin/invites", { email, role }, CreateInviteResponseSchema);
  }

  listInvites(): Promise<ListInvitesResponse> {
    return this.get("/admin/invites", ListInvitesResponseSchema);
  }

  acceptInvite(token: string, password: string, name: string): Promise<AcceptInviteResponse> {
    return this.post(
      "/accept-invite",
      { token, password, name },
      AcceptInviteResponseSchema
    );
  }

  /** UM.6.A.3 — admin näkee käyttäjälistan view-scope-pickerille. */
  listUsers(): Promise<{ users: { id: string; email: string; name: string }[] }> {
    return this.get(
      "/admin/users",
      // Inline-schema koska ListUsersResponse on yksinkertainen
      // ja vältetään edestakas-importti
      AdminUsersResponseSchema
    );
  }

  // ─── E1.3 (ADR-016 V1++): per-user-S3-cred -hallinta ───────────────

  listUserCredentials(): Promise<ListUserCredentialsResponse> {
    return this.get("/admin/users/credentials", ListUserCredentialsResponseSchema);
  }

  setUserCredential(
    email: string,
    s3AccessKeyId: string,
    s3SecretAccessKey: string,
    name?: string
  ): Promise<SetUserCredentialResponse> {
    return this.post(
      `/admin/users/${encodeURIComponent(email)}/credentials`,
      { s3AccessKeyId, s3SecretAccessKey, ...(name ? { name } : {}) },
      SetUserCredentialResponseSchema
    );
  }

  /** Pool-pop: assign next-free pool-cred käyttäjälle. */
  assignFromPool(email: string): Promise<SetUserCredentialResponse> {
    return this.post(
      `/admin/users/${encodeURIComponent(email)}/credentials`,
      {},
      SetUserCredentialResponseSchema
    );
  }

  addPoolCredential(
    s3AccessKeyId: string,
    s3SecretAccessKey: string,
    name?: string
  ): Promise<AddPoolCredentialResponse> {
    return this.post(
      "/admin/users/pool/credentials",
      { s3AccessKeyId, s3SecretAccessKey, ...(name ? { name } : {}) },
      AddPoolCredentialResponseSchema
    );
  }

  async deletePoolCredential(id: string): Promise<{ ok: boolean; id: string }> {
    const url = `${this.baseUrl}/admin/users/pool/credentials/${encodeURIComponent(id)}`;
    const res = await this.fetchFn(url, { method: "DELETE", credentials: "include" });
    if (res.status === 401) throw new NotAuthenticatedError();
    if (!res.ok) {
      const body = await safeJson(res);
      const code = typeof body?.error === "string" ? body.error : undefined;
      throw new ApiError(
        `api_error: ${res.status}${code ? ` (${code})` : ""}`,
        res.status,
        code
      );
    }
    return res.json();
  }

  async deleteUserCredential(email: string): Promise<{ ok: boolean; email: string }> {
    const url = `${this.baseUrl}/admin/users/${encodeURIComponent(email)}/credentials`;
    const res = await this.fetchFn(url, { method: "DELETE", credentials: "include" });
    if (res.status === 401) throw new NotAuthenticatedError();
    if (!res.ok) {
      const body = await safeJson(res);
      const code = typeof body?.error === "string" ? body.error : undefined;
      throw new ApiError(
        `api_error: ${res.status}${code ? ` (${code})` : ""}`,
        res.status,
        code
      );
    }
    return res.json();
  }

  // ─── ADR-020: Scaleway-provisiointi (automaattinen, korvaa Hetzner-pool) ─

  provisionScaleway(email: string): Promise<ProvisionScalewayResponse> {
    return this.post(
      `/admin/users/${encodeURIComponent(email)}/provision-scaleway`,
      undefined,
      ProvisionScalewayResponseSchema
    );
  }

  // ─── 2.5.E.1: daemon-machine -rajapinnat ──────────────────────────────

  /**
   * @param opts.scope optional view-scope override ("all" admin-näkymässä
   * jos halutaan kaikkien käyttäjien koneet, oletuksena ViewScopeProvider:n
   * arvo). Lähetetään X-Kratti-View-Scope-otsikkona.
   */
  listMachines(opts?: { scope?: string }): Promise<DaemonMachinesResponse> {
    const init: ApiCallInit = opts?.scope
      ? { headers: { "X-Kratti-View-Scope": opts.scope } }
      : {};
    return this.get("/daemon/machines", DaemonMachinesResponseSchema, init);
  }

  getMachineConfig(machineId: string): Promise<DaemonConfigResponse> {
    return this.get(
      `/daemon/machines/${encodeURIComponent(machineId)}/config`,
      DaemonConfigResponseSchema
    );
  }

  /**
   * PUT /daemon/machines/:id/config — partial-update sallittu (mainitut
   * kentat paivittyvät, muut sailyvät). Vain owner saa kirjoittaa.
   */
  async updateMachineConfig(
    machineId: string,
    body: { sourceRoots?: string[]; pacingMode?: "polite" | "balanced" | "fast"; additionalOptions?: Record<string, unknown> }
  ): Promise<DaemonConfigResponse> {
    const url = `${this.baseUrl}/daemon/machines/${encodeURIComponent(machineId)}/config`;
    const res = await this.fetchFn(url, {
      method: "PUT",
      credentials: "include",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify(body),
    });
    if (res.status === 401) throw new NotAuthenticatedError();
    if (!res.ok) {
      const data = await safeJson(res);
      const code = typeof data?.error === "string" ? data.error : undefined;
      throw new ApiError(
        `api_error: ${res.status}${code ? ` (${code})` : ""}`,
        res.status,
        code
      );
    }
    const data = (await res.json()) as unknown;
    return DaemonConfigResponseSchema.parse(data);
  }

  getMachineDisks(machineId: string): Promise<DaemonDisksResponse> {
    return this.get(
      `/daemon/machines/${encodeURIComponent(machineId)}/disks`,
      DaemonDisksResponseSchema
    );
  }

  /**
   * DELETE /daemon/machines/:id — poistaa koneen + cascade (daemon_disk,
   * daemon_inbox). Vain owner (admin view-scope=all bypass:lla).
   */
  async deleteMachine(machineId: string): Promise<{ deleted: boolean; machineId: string }> {
    const url = `${this.baseUrl}/daemon/machines/${encodeURIComponent(machineId)}`;
    const res = await this.fetchFn(url, {
      method: "DELETE",
      credentials: "include",
      headers: { ...this.viewScopeHeaders(), accept: "application/json" },
    });
    if (res.status === 401) throw new NotAuthenticatedError();
    if (!res.ok) {
      const data = await safeJson(res);
      const code = typeof data?.error === "string" ? data.error : undefined;
      throw new ApiError(
        `api_error: ${res.status}${code ? ` (${code})` : ""}`,
        res.status,
        code
      );
    }
    return (await res.json()) as { deleted: boolean; machineId: string };
  }

  // ─── 3.A.7.4: inbox-pyynnöt ──────────────────────────────────────────

  async postInboxRequest(
    machineId: string,
    body: { type: string; request: Record<string, unknown> }
  ): Promise<InboxRequestCreated> {
    return this.post(
      `/daemon/machines/${encodeURIComponent(machineId)}/inbox`,
      body,
      InboxRequestCreatedSchema
    );
  }

  getInboxStatus(requestId: string): Promise<InboxStatus> {
    return this.get(
      `/daemon/inbox/${encodeURIComponent(requestId)}/status`,
      InboxStatusSchema
    );
  }

  private async get<T>(path: string, schema: ZodType<T>, init: ApiCallInit = {}): Promise<T> {
    const url = this.buildUrl(path, init.query);
    let res: Response;
    try {
      res = await this.fetchFn(url, {
        method: "GET",
        credentials: "include",
        headers: {
          accept: "application/json",
          ...this.viewScopeHeaders(),
          ...(init.headers ?? {}),
        },
        ...stripQueryAndHeaders(init),
      });
    } catch (err) {
      throw new ApiError(
        `network_error: ${err instanceof Error ? err.message : String(err)}`,
        0,
        "network_error"
      );
    }

    if (res.status === 401) throw new NotAuthenticatedError();
    if (!res.ok) {
      const body = await safeJson(res);
      const code = typeof body?.error === "string" ? body.error : undefined;
      throw new ApiError(`api_error: ${res.status}${code ? ` (${code})` : ""}`, res.status, code);
    }

    let data: unknown;
    try {
      data = await res.json();
    } catch {
      throw new ApiError("invalid_json_response", res.status, "invalid_json");
    }

    const parsed = schema.safeParse(data);
    if (!parsed.success) {
      throw new ApiError(
        `invalid_response_body: ${parsed.error.issues
          .map((i) => `${i.path.join(".")}: ${i.message}`)
          .join("; ")}`,
        res.status,
        "invalid_response_body"
      );
    }
    return parsed.data;
  }

  private async post<T>(path: string, body: unknown, schema: ZodType<T>): Promise<T> {
    const url = `${this.baseUrl}${path}`;
    let res: Response;
    const init: RequestInit = {
      method: "POST",
      credentials: "include",
      headers: {
        accept: "application/json",
        "content-type": "application/json",
        ...this.viewScopeHeaders(),
      },
    };
    if (body !== undefined) init.body = JSON.stringify(body);
    try {
      res = await this.fetchFn(url, init);
    } catch (err) {
      throw new ApiError(
        `network_error: ${err instanceof Error ? err.message : String(err)}`,
        0,
        "network_error"
      );
    }

    if (res.status === 401) throw new NotAuthenticatedError();
    if (!res.ok) {
      const errBody = await safeJson(res);
      const code = typeof errBody?.error === "string" ? errBody.error : undefined;
      throw new ApiError(`api_error: ${res.status}${code ? ` (${code})` : ""}`, res.status, code);
    }

    let data: unknown;
    try {
      data = await res.json();
    } catch {
      throw new ApiError("invalid_json_response", res.status, "invalid_json");
    }
    const parsed = schema.safeParse(data);
    if (!parsed.success) {
      throw new ApiError(
        `invalid_response_body: ${parsed.error.issues
          .map((i) => `${i.path.join(".")}: ${i.message}`)
          .join("; ")}`,
        res.status,
        "invalid_response_body"
      );
    }
    return parsed.data;
  }

  private buildUrl(path: string, query?: Record<string, string | number | undefined>): string {
    const url = `${this.baseUrl}${path}`;
    if (!query) return url;
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined) continue;
      params.set(k, String(v));
    }
    const qs = params.toString();
    return qs ? `${url}?${qs}` : url;
  }
}

function stripQueryAndHeaders(init: ApiCallInit): Omit<RequestInit, "body" | "method"> {
  const { query: _q, headers: _h, ...rest } = init;
  return rest;
}

async function safeJson(res: Response): Promise<{ error?: unknown } | null> {
  try {
    return (await res.json()) as { error?: unknown };
  } catch {
    return null;
  }
}

/** Default-instanssi web-appille (window.location.origin -base). */
export const apiClient = new ApiClient();
